<script>
    import Modal from 'views/components/modals/Modal.svelte'
    import Button from 'views/components/button/Button.svelte'
    import Avatar from 'views/components/avatar/Avatar.svelte'
    import Icon from 'views/components/icon/Icon.svelte'
    import QuickInput from 'views/components/quickInput/QuickInput.svelte'
    import saveEmail from 'views/components/modals/fixEmail/saveEmail'
    import Util from 'util/util'

    /**
     * @typedef {Object} Props
     * @property {Function} [onStudentCountChange] - Called with the number of students that are
     * still without credentials, each time a student has been helped.
     */

    /** @type {Props} */
    const { onStudentCountChange = null } = $props()

    // Shared name of the <details> elements, so only one student can be opened at a time
    const detailsName = _.uniqueId('students-without-credentials-')

    let isModalOpen = $state(true)
    let students = $state([])

    // Student whose email is being corrected
    let editingStudentId = $state(null)
    let editedEmail = $state('')
    let emailInput = $state(null)
    let isSubmitting = $state(false)

    const request = loadStudents()

    // The endpoint only returns ids, the student details are already in the global students collection
    async function loadStudents() {
        const studentIds = await jQuery.getJSON('/users/get_students_without_credentials.json')

        const compareNames = (a, b) => a.last_name_first_name().localeCompare(
            b.last_name_first_name(),
            window.app_version.language,
            { sensitivity: 'base' }
        )

        students = studentIds
            .map((studentId) => Backbone.Collection.students.get(studentId))
            .filter(Boolean)
            .sort(compareNames)
            .map((studentModel) => studentModel.toJSON())
    }

    function studentName(student) {
        return Util.lastNameFirstName(student.first_name, student.prefix, student.sortable_last_name)
    }

    function onToggle(event, student) {
        if (event.currentTarget.open) {
            editingStudentId = student.id
            editedEmail = student.email ?? ''
        } else if (editingStudentId === student.id) {
            stopEditing()
        }
    }

    function stopEditing() {
        editingStudentId = null
        editedEmail = ''
    }

    function removeStudent(studentId) {
        students = students.filter((student) => student.id !== studentId)
        onStudentCountChange?.(students.length)
    }

    // Only called by the email input after it validated the email, it tells the user when it's empty or invalid
    async function resendCredentials(student) {
        isSubmitting = true
        const result = await saveEmail(editedEmail, student.id)
        isSubmitting = false

        if (result === 'saved') {
            Backbone.Collection.students.get(student.id)?.set('email', editedEmail)
        }
        if (result === 'saved' || result === 'already-valid') {
            stopEditing()
            removeStudent(student.id)
        }
    }
</script>

<Modal
    title={window.i18n.gettext('Didn\'t receive login credentials')}
    canBeClosedWithEscapeKey={true}
    bind:isOpen={isModalOpen}
    {request}
>
    {#if students.length}
        <div class="introduction-text">
            {window.i18n.gettext(
                'We couldn\'t deliver the login credentials to these students. Usually the email address contains ' +
                'a typo, or the mailbox doesn\'t accept our email. Check the email address and send the login ' +
                'credentials again.'
            )}
        </div>

        <div class="students">
            {#each students as student (student.id)}
                <details
                    name={detailsName}
                    open={editingStudentId === student.id}
                    ontoggle={(event) => onToggle(event, student)}
                >
                    <summary class="student-row">
                        <div class="student">
                            <Avatar avatar={student.avatar} showOnlineState={false}></Avatar>
                            <span class="name">{studentName(student)}</span>
                        </div>
                        <span class="toggle">
                            <Icon name="pencil"></Icon>
                            <span>{window.i18n.gettext('Check email address')}</span>
                        </span>
                    </summary>

                    {#if editingStudentId === student.id}
                        <div class="edit-row">
                            <div class="email-input">
                                <QuickInput
                                    bind:this={emailInput}
                                    bind:value={editedEmail}
                                    type="email"
                                    label={window.i18n.gettext('Email address')}
                                    autoFocus={true}
                                    noAutoCorrect={true}
                                    noAutoCapitalize={true}
                                    disabled={isSubmitting}
                                    submitCallback={() => resendCredentials(student)}
                                    showSubmitButton={false}
                                ></QuickInput>
                            </div>
                            <div class="edit-actions">
                                <Button
                                    label={window.i18n.gettext('Cancel')}
                                    theme="secondary"
                                    disabled={isSubmitting}
                                    callback={stopEditing}
                                ></Button>
                                <Button
                                    label={window.i18n.gettext('Resend login information')}
                                    disabled={isSubmitting}
                                    callback={() => emailInput?.triggerSubmitCallback()}
                                ></Button>
                            </div>
                        </div>
                    {/if}
                </details>
            {/each}
        </div>
    {:else}
        <p class="empty">{window.i18n.gettext('All students have received their login credentials.')}</p>
    {/if}

    {#snippet buttons()}
        <Button
            label={window.i18n.gettext('Close')}
            theme="secondary"
            callback={() => { isModalOpen = false }}
        ></Button>
    {/snippet}
</Modal>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
.introduction-text {
  font-size: 16px;
  margin-bottom: 24px;
  color: rgba(18, 45, 64, 0.8);
  contain: inline-size;
}

.students {
  display: flex;
  flex-direction: column;
  border: 1px solid hsl(27, 15%, 93%);
  border-radius: 12px;
  overflow: hidden;
}

details + details {
  border-top: 1px solid hsl(27, 15%, 93%);
}

.student-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 4px 12px;
  list-style: none;
  cursor: pointer;
}
.student-row::-webkit-details-marker {
  display: none;
}

.student {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.student .name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.toggle {
  font-size: 14px;
  border-radius: 12px;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  transition: background 0.2s cubic-bezier(0.215, 0.61, 0.355, 1);
}
.toggle :global(svg) {
  width: 16px;
  height: 16px;
  transition: fill 0.1s cubic-bezier(0.215, 0.61, 0.355, 1);
  flex-shrink: 0;
}
.toggle {
  background-color: transparent;
  color: unset;
}
.toggle > :global(*) {
  display: inline-flex;
}
.toggle > :global(*:empty) {
  display: none;
}
@media (hover: hover) {
  .toggle:hover {
    background-color: hsl(205, 60%, 44%);
    border-color: hsl(205, 60%, 44%);
    color: #fff;
  }
  .toggle:hover :global(svg) {
    fill: #fff;
  }
}
.toggle {
  flex-shrink: 0;
}
details[open] .toggle {
  visibility: hidden;
}

.edit-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
  padding: 4px 12px 12px;
}
.edit-row .email-input {
  flex: 1 1 240px;
  min-width: 0;
}
.edit-row .edit-actions {
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.empty {
  font-size: 16px;
  padding: 24px 0;
  color: rgba(18, 45, 64, 0.8);
  text-align: center;
}</style>
